import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Page } from '../../components';
import { AdminTable } from '../../components/table/AdminTable';
import { IListingInteractionTypesAPI, IActivityTypesAPI } from '../../fetch';
import { useToastContext } from '../../hooks';
import { IListingInteractionType } from '../../models';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { ILookupType } from '../../models/employee';
import { ListingInteractionTypesFilters } from './ListingInteractionTypesFilters';
import { ListingInteractionTypesRankCell } from './ListingInteractionTypesRankCell';
import { ListingInteractionTypesNameCell } from './ListingInteractionTypesNameCell';
import { ListingInteractionTypesDescriptionCell } from './ListingInteractionTypesDescriptionCell';


export const ListingInteractionTypes: FC = () => {
  const { showToast } = useToastContext();

  const [listingInteractionTypes, setListingInteractionTypes] = useState<IListingInteractionType[]>([]);
  const [isLoadingListingInteractionTypes, setIsLoadingListingInteractionTypes] = useState(false);
  const [isSavingListingInteractionTypes, setIsSavingListingInteractionTypes] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  // If not false, this is the row the user seeks to delete
  const [isDeletingListingInteractionType, setIsDeletingListingInteractionType] = useState<false | IListingInteractionType>(false);

  const [selectedActivityTypeId, setSelectedActivityTypeId] = useState<null | number>(null);

  const [filters, setFilters] = useState<{
    activityTypeId?: null | number;
  }>({ activityTypeId: selectedActivityTypeId });

  const removeRow = (row: IListingInteractionType): void => {
    setListingInteractionTypes((state: IListingInteractionType[]) => {
      const index = state.indexOf(row);
      const newState = state.slice();
      // Cut out the deleted row
      newState.splice(index, 1);
      return newState;
    });
  };

  const { isLoading: isLoadingActivityTypes, data: activityTypes } = useQuery<ILookupType[], Error>({
    queryKey: ['activityTypes'],
    queryFn: async () => {
      const data = await IActivityTypesAPI.getAll().then(x => {
        return x.map(v => { return {text: v.name, description: v.name, value: v.activityTypeId}; }) as ILookupType[]; 
      });
      return data;
    },
    onError(error: any) {
      showToast('error', error?.detail || 'Error trying to load activity types. Please try again.');
    }
  });

  useEffect(() => {
    if (activityTypes?.length) {
      setSelectedActivityTypeId(+activityTypes[0].value);
    }
  }, [activityTypes]);

  const fetchListingInteractionTypesOnMount = useCallback(async () => {
    setIsLoadingListingInteractionTypes(true);
    try {
      if(selectedActivityTypeId !== null && selectedActivityTypeId > 0)
        setListingInteractionTypes((await IListingInteractionTypesAPI.getAll()).filter(x => x.activityTypeId === selectedActivityTypeId));
      else
        setListingInteractionTypes((await IListingInteractionTypesAPI.getAll()));
    } catch (err) {
      const message = err instanceof Error && err.message;
      showToast('error', message || 'Failed to fetch listing interaction types.');
    } finally {
        setIsLoadingListingInteractionTypes(false);
    }
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActivityTypeId]);

  useEffect(() => {
    fetchListingInteractionTypesOnMount();
  }, [fetchListingInteractionTypesOnMount]); 


  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const handleNameChange = (index: number, newName:  string) => {
    setListingInteractionTypes((state: IListingInteractionType[]) => {
      const updatedState = [...state];
      
      if (updatedState[index].name !== newName) {
        setHasChanges(true);
      }

      updatedState[index].name = newName;
      return updatedState;
    });
  };

  const handleDescriptionChange = (index: number, newDescription:  string) => {
    setListingInteractionTypes((state: IListingInteractionType[]) => {
      const updatedState = [...state];
      
      if (updatedState[index].description !== newDescription) {
        setHasChanges(true);
      }

      updatedState[index].description = newDescription;
      return updatedState;
    });
  };

  const handleRankChange = (index: number, newRank: number | null) => {
    setListingInteractionTypes((state: IListingInteractionType[]) => {
      const updatedState = [...state];
      
      if (updatedState[index].rank !== newRank) {
        setHasChanges(true);
      }

      updatedState[index].rank = newRank;
      return updatedState;
    });
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target?.select?.();
  };

  const columns = useMemo(() => {
    return [
        {
            accesor: 'name',
            Header: 'Listing Interaction Type Name',
            Cell: ({
              cell: {
                row: { original, index },
              },
            }: {
              cell: { row: { original: IListingInteractionType; index: number } };
            }) => 
              <ListingInteractionTypesNameCell 
                key={index}
                name={original.name}
                index={index}
                onNameChange={handleNameChange}
                onFocus={handleFocus}
              />,
          },
      {
        accesor: 'description',
        Header: 'Description',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IListingInteractionType; index: number } };
        }) => 
          <ListingInteractionTypesDescriptionCell
            key={index}
            description={original.description}
            index={index}
            onDescriptionChange={handleDescriptionChange}
            onFocus={handleFocus}
          />,
      },
      {
        accesor: 'rank',
        Header: 'Rank',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IListingInteractionType; index: number } };
        }) => 
        <ListingInteractionTypesRankCell 
          key={index}
          rank={original.rank}
          index={index}
          onRankChange={handleRankChange}
          onFocus={handleFocus}
        />,
      },
    ];
  }, []);

  return (
    <Page title="Listing Interaction Types">
      <ListingInteractionTypesFilters 
        isLoading={
          isLoadingActivityTypes
        }
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters && activityTypes?.[0].value !== undefined) {
            setFilters({
              activityTypeId: +activityTypes?.[0].value || null
            });
          } else {
            setFilters({
              ...filters,
              activityTypeId: selectedActivityTypeId ?? null,
            });
          }
        }}
        activityTypes={activityTypes ?? []}
        selectedActivityType={selectedActivityTypeId}
        setSelectedActivityType={setSelectedActivityTypeId}
        hasAppliedFilters={hasAppliedFilters}
        setHasAppliedFilters={setHasAppliedFilters}
      />
      <AdminTable
        data={listingInteractionTypes}
        columns={columns}
        isLoading={isLoadingListingInteractionTypes}
        hasChanges={hasChanges}
        isSaving={isSavingListingInteractionTypes}
        onAdd={() => {
          let listingInteractionType = IListingInteractionType.getDefault();
          listingInteractionType.activityTypeId = selectedActivityTypeId;
            // Add new empty row
            setListingInteractionTypes(state => state.concat(listingInteractionType));
          }}
        onSave={async function upsertActivityTypes() {
          setIsSavingListingInteractionTypes(true);
          try {
            
            const updatedList: IListingInteractionType[] = (await IListingInteractionTypesAPI.upsert(listingInteractionTypes)).filter(x => x.activityTypeId === selectedActivityTypeId);
            setListingInteractionTypes(updatedList);
            setHasChanges(false);
            showToast('success', 'Listing Interaction Type was updated successfully!');
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update listing interaction types.';
            showToast('error', message);
          } finally {
            setIsSavingListingInteractionTypes(false);
          }
        }}
        onDelete={(original, _index) => {
            if (original.listingInteractionTypeId === IListingInteractionType.getDefault().listingInteractionTypeId) {
              removeRow(original);
              showToast('info', 'Removed unsaved listing interaction type.');
            } else {
                setIsDeletingListingInteractionType(original);
            }
          }}
      />

      {/** ConfirmationDialogue */}
      {!!isDeletingListingInteractionType && (
        <ConfirmationDialogue
          open
          id="delete-listing-interaction-type"
          title={`Delete Listing Interaction Type ${isDeletingListingInteractionType.name}`}
          onClose={() => setIsDeletingListingInteractionType(false)}
          onConfirm={async function deleteListingInteractionType() {
            // Check if modal is open and has proper data
            if (!isDeletingListingInteractionType) {
              return;
            }
            // If this is a new, not-yet-saved entry, it does not exist in the DB
            if (
              isDeletingListingInteractionType.listingInteractionTypeId ===
              IListingInteractionType.getDefault().listingInteractionTypeId
            ) {
              // So, deletion only consists of removal from local state
              removeRow(isDeletingListingInteractionType);
            } else {
              // Otherwise this entry already exists, hit the API to delete it
              try {
                await IListingInteractionTypesAPI.delete(isDeletingListingInteractionType.listingInteractionTypeId);
                // Update state after only successful delete
                removeRow(isDeletingListingInteractionType);
                showToast('success', 'Successfully deleted listing interaction type.');
              } catch (error) {
                const message =
                  error instanceof Error
                    ? error.message
                    : displayFailure('delete', 'listing interaction type');
                showToast('error', message);
              }
            }

            // Close modal
            setIsDeletingListingInteractionType(false);
          }}
        />
      )}
    </Page>
  );
};
function displayFailure(operation: 'get' | 'update' | 'delete', resource: string): string {
    // Verify message ends with a period.
    const resource_ = resource.endsWith('.') ? resource.slice(0, resource.length - 1) : resource;
  
    return `Failed to ${operation} ${resource_}.`;
  }